"use client";

import logo2 from "@/assets/Logo.png";
import innova from "@/assets/Logo2.png";
import logoEmpresaDefault from "@/assets/Logo_Default.png";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Tab } from "./Form/Login/Types/types";
import RegisterNewClient from "./Form/Register/ClientRegister";

const logoEmpresaPath =
  process.env.NEXT_PUBLIC_LOGO_EMPRESA || logoEmpresaDefault;

interface BaseLoginLayoutProps {
  children?: React.ReactNode;
  defaultTab?: Tab;
  mode?: "auth" | "recover";
  title?: string;
  subtitle?: string;
  brandingTitle?: string;
  brandingText?: string;
}

const BaseLoginLayout: React.FC<BaseLoginLayoutProps> = ({
  children,
  defaultTab = "login",
  mode = "auth",
  title,
  subtitle,
  brandingTitle,
  brandingText,
}) => {
  const searchParams = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = mode === "recover" ? "login" : (tabParam === "register" ? "register" : defaultTab);

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const particlesDesktop = useMemo(
    () =>
      mounted
        ? Array.from({ length: 35 }, () => ({
          x: Math.random() * 100,
          top: Math.random() * 100,
          size: 45 + Math.random() * 60,
          duration: 6 + Math.random() * 6,
          opacity: 0.1 + Math.random() * 0.3,
          delay: Math.random() * 6,
        }))
        : [],
    [mounted]
  );

  const particlesMobile = useMemo(
    () =>
      mounted
        ? Array.from({ length: 35 }, () => ({
          x: Math.random() * 100,
          top: Math.random() * 100,
          size: 30 + Math.random() * 60,
          duration: 6 + Math.random() * 6,
          opacity: 0.1 + Math.random() * 0.3,
          delay: Math.random() * 6,
        }))
        : [],
    [mounted]
  );

  const router = useRouter();

  const handleTabClick = (t: Tab) => {
    router.replace(`/login?tab=${t}`);
  };

  return (
    <div
      className={`h-screen flex bg-[var(--color-bg)] overflow-x-hidden overflow-y-auto lg:overflow-y-hidden transition-all duration-700 ${tab === "register" ? "flex-row-reverse" : ""
        }`}
    >
      {/* ==================== LADO IZQUIERDO - BRANDING (Desktop) ==================== */}
      <div className="hidden lg:flex relative flex-col justify-between w-[45%] branding-overlay text-white overflow-hidden transition-all duration-700 bg-[var(--color-primary-per2)]">
        {/* Logo flotante */}
        <div
          className={`absolute bottom-[170px] z-10 bg-white shadow-md border border-gray-400 flex items-center ${tab === "register"
            ? "right-0 rounded-l-[20px]"
            : "left-0 rounded-r-[20px]"
            }`}
        >
          <Image
            src={logo2}
            alt="Logo"
            width={350}
            height={200}
            className="h-[60px] w-auto m-3 mt-6 object-contain"
          />
        </div>

        {/* Círculos de fondo con blur */}
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[var(--color-primary-light)]/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl transition-all duration-1000 ${tab === "register"
            ? "bg-[var(--color-primary-light)]/15 scale-150"
            : "bg-white/5 scale-100"
            }`}
        />

        {/* Partículas flotantes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particlesDesktop.map((p, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float-up"
              style={{
                left: `${p.x}%`,
                top: `${p.top}%`,
                width: p.size,
                height: p.size,
                animationDelay: `-${p.delay}s`,
                animationDuration: `${p.duration}s`,
                opacity: p.opacity,
                filter: `blur(${p.size > 40 ? 3 : 1}px)`,
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.25) 100%, rgba(255,255,255,0.05) 40%, transparent 100%)",
              }}
            />
          ))}
        </div>

        {/* Contenido central */}
        <div className="relative z-10 flex flex-col justify-center px-14 py-14 text-center mt-30">
          <div className="mb-10 flex justify-center">
            <div className="relative w-[180px] sm:w-[220px] md:w-[260px] lg:w-[320px] aspect-[4/3] mt-2">
              <Image
                src={logoEmpresaPath}
                alt="Logo"
                fill
                className="object-contain"
                priority
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-4">
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight">
              {brandingTitle || (tab === "login" ? "¡Bienvenido de nuevo!" : "¡Únete ahora!")}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed max-w-prose">
              {brandingText || (tab === "login"
                ? "Tu salud, más cerca que nunca. Agenda citas, revisa tus exámenes y accede a tu información médica fácilmente."
                : "Crea tu cuenta y comienza a gestionar tus citas, resultados y toda tu información de salud en un solo lugar.")}
            </p>
          </div>
        </div>
      </div>

      {/* ==================== LADO DERECHO ==================== */}
      <div className="flex flex-1 flex-col transition-all duration-700 lg:overflow-y-auto">
        {/* Mobile branding */}
        <div className="flex-shrink-0 text-white pt-4 pb-6 px-6 rounded-b-[40px] transition-all duration-700 relative overflow-hidden branding-overlay lg:hidden">
          <div
            key={`infobar-${tab}`}
            className={tab === "login" ? "info-bar-login" : "info-bar-register"}
          />

          {/* Blobs dinámicos */}
          <div
            className={`absolute rounded-full bg-[var(--color-primary-light)]/20 blur-3xl transition-all duration-700 ${tab === "register"
              ? "-top-10 -right-6 w-52 h-52 scale-125"
              : "-top-14 -right-10 w-44 h-44"
              }`}
          />
          <div
            className={`absolute rounded-full bg-white/5 blur-3xl transition-all duration-700 ${tab === "register"
              ? "-bottom-6 left-4 w-32 h-32 scale-125"
              : "-bottom-8 left-6 w-24 h-24"
              }`}
          />
          <div
            className={`absolute rounded-full blur-2xl transition-all duration-700 ${tab === "register"
              ? "top-8 left-16 w-20 h-20 bg-[var(--color-primary-light)]/40 scale-150"
              : "top-6 left-14 w-14 h-14 bg-[var(--color-primary-light)]/30"
              }`}
          />

          {/* Glow central */}
          <div
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-all duration-1000 ${tab === "register"
              ? "w-52 h-52 bg-[var(--color-primary-light)]/20 scale-125"
              : "w-40 h-40 bg-white/10 scale-100"
              }`}
          />

          {/* Partículas flotantes mobile */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particlesMobile.map((p, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  left: `${p.x}%`,
                  top: `${p.top}%`,
                  width: p.size,
                  height: p.size,
                  animationDelay: `-${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                  opacity: p.opacity,
                  filter: `blur(1px)`,
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.25) 100%, rgba(255,255,255,0.05) 40%, transparent 100%)",
                }}
              />
            ))}
          </div>

          {/* Contenido mobile */}
          <div className="flex flex-col items-center text-center relative z-10">
            <div
              className={`relative h-46 w-64 max-w-[80vw] transition-transform duration-300 ${tab === "login" ? "scale-100" : "scale-110"
                }`}
            >
              <Image
                src={logoEmpresaPath}
                alt="Logo"
                fill
                className="object-contain"
              />
            </div>
            <div key={mode === "recover" ? "recover" : tab} className="mt-3 animate-fadeUp">
              <h1 className="text-2xl font-extrabold tracking-tight">
                {brandingTitle || (tab === "login" ? "¡Bienvenido de nuevo!" : "¡Únete ahora!")}
              </h1>
              <p className="text-sm text-white/70 mt-1 max-w-xs">
                {brandingText || (tab === "login"
                  ? "Tu salud, más cerca que nunca. Agenda citas, revisa tus exámenes y accede a tu información médica fácilmente."
                  : "Crea tu cuenta y comienza a gestionar tus citas, resultados y toda tu información de salud en un solo lugar.")}
              </p>
            </div>
          </div>
        </div>

        {/* Área del contenido */}
        <div className="flex-1 flex justify-center items-start lg:items-center px-6 sm:px-10 mt-5 lg:mt-0 pb-8 lg:pb-0">
          <div className="w-full max-w-lg flex flex-col gap-6 mb-4 px-2">
            {/* Título */}
            <div
              key={`title-${mode === "recover" ? "recover" : tab}`}
              className="mb-2 animate-fade-up text-center"
            >
              <h2 className="text-2xl font-bold text-[var(--color-text)] tracking-tight">
                {title || (tab === "login" ? "Iniciar sesión" : "Crear cuenta")}
              </h2>
              <p className="text-normal text-[var(--color-text-light)]">
                {subtitle || (tab === "login"
                  ? "Ingresa tus credenciales para continuar"
                  : "Completa el formulario para registrarte")}
              </p>
            </div>

            {/* Tabs - Solo mostrar en modo auth */}
            {mode === "auth" && (
              <div
                className="relative grid grid-cols-2 bg-[var(--color-muted)] rounded-full p-1 border border-[var(--color-gray-border-pr)] overflow-hidden"
                style={{ isolation: "isolate" }}
              >
                <div
                  className="absolute top-1 bottom-1 w-[calc(50%-6px)] rounded-full bg-[var(--color-primary)] shadow-md transition-all duration-300 ease-in-out"
                  style={{
                    left: tab === "login" ? "4px" : "calc(50% + 2px)",
                  }}
                />
                {(["login", "register"] as Tab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => handleTabClick(t)}
                    className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-full transition-colors duration-300 cursor-pointer ${tab === t
                      ? "text-white"
                      : "text-[var(--color-text-light)] hover:text-[var(--color-text)]"
                      }`}
                  >
                    {t === "login" ? "Iniciar sesión" : "Registrarse"}
                  </button>
                ))}
              </div>
            )}

            {/* Contenido del tab */}
            {mode === "recover" ? (
              children
            ) : (
              tab === "login" ? (
                children
              ) : (
                <RegisterNewClient />
              )
            )}

            {/* Logo Innova */}
            <div className="mt-2 mb-4 flex justify-center pb-2">
              <Image src={innova} alt="Logo Innova" className="h-12 w-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseLoginLayout;
